// lib/pwa/sw.ts
// THE service worker, built by Serwist (app/serwist/[path]/route.ts) and served at /sw.js, scope "/".
// §66: offline-first app shell, queued actions, web push. PROTECTED: it sits in front of every page.
//
// What it does, deliberately small:
//   - Push and notification taps, exactly as the old hand-written /sw.js did (Lane J's alerts).
//   - Hashed Next assets (/_next/static): cache first; they never change under the same name.
//   - Her screens (/dashboard...): network first with a 3 s timeout, then the last copy she saw,
//     so the app opens even with no signal (DESIGN.md §8.1). Cleared on log out (lib/pwa/register).
//   - Any other page that fails offline: the precached /offline page.
//   - /api/*: never cached (her data is read live, never served stale from a cache).
//   - Queued actions: a seam. Routes listed in QUEUED_ACTION_ROUTES (./queued-actions) are retried
//     by Background Sync when the signal returns. EMPTY today: a route joins only once its handler
//     is idempotent (a retried "Add booking" must never make two bookings), Lane B's call per route.
// Only the offline page (and, on install, the few files it names) is saved ahead of time, not every
// chunk: installing must stay cheap on a ₱6–8k phone.

import {
  BackgroundSyncPlugin, CacheFirst, ExpirationPlugin, NetworkFirst, NetworkOnly, Serwist, StaleWhileRevalidate,
  type PrecacheEntry, type RuntimeCaching,
} from "serwist";
// This file must export nothing (see ./queued-actions.ts): an export survives into /sw.js as ESM.
import { QUEUED_ACTION_ROUTES } from "./queued-actions";

type PushData = { title?: string; body?: string; type?: string; url?: string };
type SwEvent = Event & { waitUntil(p: Promise<unknown>): void };
type PushEvt = SwEvent & { data: { json(): unknown } | null };
type ClickEvt = SwEvent & { notification: Notification & { data?: { url?: string } } };
type WindowClientLike = { url: string; focus(): Promise<unknown>; navigate(url: string): Promise<unknown> };
type SwScope = {
  __SW_MANIFEST: (PrecacheEntry | string)[] | undefined;
  registration: ServiceWorkerRegistration;
  clients: { matchAll(o: { type: "window"; includeUncontrolled: boolean }): Promise<WindowClientLike[]>; openWindow(url: string): Promise<unknown> };
  addEventListener(type: "push", l: (e: PushEvt) => void): void;
  addEventListener(type: "notificationclick", l: (e: ClickEvt) => void): void;
  addEventListener(type: "install", l: (e: SwEvent) => void): void;
};
const sw = self as unknown as SwScope;

const isOwnerScreen = (pathname: string) => pathname === "/dashboard" || pathname.startsWith("/dashboard/");

const runtimeCaching: RuntimeCaching[] = [
  {
    matcher: ({ sameOrigin, url }) => sameOrigin && url.pathname.startsWith("/_next/static/"),
    handler: new CacheFirst({
      cacheName: "bizai-static",
      plugins: [new ExpirationPlugin({ maxEntries: 200, maxAgeSeconds: 30 * 24 * 60 * 60, maxAgeFrom: "last-used" })],
    }),
  },
  {
    matcher: ({ sameOrigin, url }) => sameOrigin && /\.(?:woff2|webp|png|svg|ico)$/i.test(url.pathname) && !url.pathname.startsWith("/api/"),
    handler: new StaleWhileRevalidate({
      cacheName: "bizai-assets",
      plugins: [new ExpirationPlugin({ maxEntries: 60, maxAgeSeconds: 30 * 24 * 60 * 60 })],
    }),
  },
  ...QUEUED_ACTION_ROUTES.map((route): RuntimeCaching => ({
    matcher: ({ sameOrigin, url }) => sameOrigin && url.pathname === route,
    method: "POST",
    handler: new NetworkOnly({ plugins: [new BackgroundSyncPlugin("bizai-actions", { maxRetentionTime: 24 * 60 })] }),
  })),
  {
    matcher: ({ sameOrigin, url }) => sameOrigin && url.pathname.startsWith("/api/"),
    handler: new NetworkOnly(),
  },
  {
    // Her screens' data requests during client-side navigation (React Server Components).
    matcher: ({ request, sameOrigin, url }) => sameOrigin && request.headers.get("RSC") === "1" && isOwnerScreen(url.pathname),
    handler: new NetworkFirst({
      cacheName: "bizai-rsc",
      networkTimeoutSeconds: 3,
      plugins: [new ExpirationPlugin({ maxEntries: 30, maxAgeSeconds: 7 * 24 * 60 * 60 })],
    }),
  },
  {
    matcher: ({ request, sameOrigin, url }) => sameOrigin && request.mode === "navigate" && isOwnerScreen(url.pathname),
    handler: new NetworkFirst({
      cacheName: "bizai-pages",
      networkTimeoutSeconds: 3,
      plugins: [new ExpirationPlugin({ maxEntries: 20, maxAgeSeconds: 7 * 24 * 60 * 60 })],
    }),
  },
  {
    matcher: ({ request }) => request.mode === "navigate",
    handler: new NetworkOnly(),
  },
];

const serwist = new Serwist({
  // Serwist fills in the precache list (the /offline page) by replacing this exact expression,
  // `self.__SW_MANIFEST`, at build time. Through an alias (sw.__SW_MANIFEST) it stays undefined.
  precacheEntries: (self as unknown as SwScope).__SW_MANIFEST,
  skipWaiting: true,
  clientsClaim: true,
  navigationPreload: true,
  runtimeCaching,
  fallbacks: {
    // /offline is precached (with the build as its revision) by app/serwist/[path]/route.ts.
    entries: [{ url: "/offline", matcher: ({ request }) => request.destination === "document" }],
  },
});

serwist.addEventListeners();

// ---- The offline page's own files ------------------------------------------------------------
// Only /offline's HTML is precached. It shows exactly when there is no signal, so its scripts,
// styles, fonts and picture would never have been fetched, and without them Next swaps the page for
// its own "This page couldn't load". On install, save the files that page names into the caches the
// routes above already read (a /_next/static file never changes under the same name).
const OFFLINE_PAGE_FILE = /(?:src|href)="(\/_next\/static\/[^"]+|\/bizi\/[^"]+)"/g;

async function saveOfflinePageFiles(): Promise<void> {
  try {
    const html = await (await fetch("/offline", { cache: "no-store" })).text();
    const urls = [...new Set(Array.from(html.matchAll(OFFLINE_PAGE_FILE), (m) => m[1]))];
    const [statics, assets] = await Promise.all([caches.open("bizai-static"), caches.open("bizai-assets")]);
    await Promise.all(urls.map((u) => (u.startsWith("/_next/static/") ? statics : assets).add(u).catch(() => {})));
  } catch {
    // No signal while installing: the offline page still shows, only less styled. Never block install.
  }
}

sw.addEventListener("install", (event) => event.waitUntil(saveOfflinePageFiles()));

// ---- Push (unchanged behaviour from the old /sw.js) --------------------------------------------

sw.addEventListener("push", (event) => {
  let data: PushData = {};
  try { data = (event.data ? event.data.json() : {}) as PushData; } catch { data = {}; }
  event.waitUntil(sw.registration.showNotification(data.title || "Ownwork", {
    body: data.body || "",
    icon: "/icon-192.png",
    badge: "/icon-192.png",
    tag: data.type || "bizai",
    renotify: true,
    data: { url: data.url || "/dashboard" },
  } as NotificationOptions));
});

sw.addEventListener("notificationclick", (event) => {
  event.notification.close();
  const target = event.notification.data?.url || "/dashboard";
  event.waitUntil(
    sw.clients.matchAll({ type: "window", includeUncontrolled: true }).then((list) => {
      for (const client of list) {
        if (client.url.includes("/dashboard")) { void client.navigate(target); return client.focus(); }
      }
      return sw.clients.openWindow(target);
    }),
  );
});
